/**
 * A STOP IN HER OWN WORDS, HELD FOR THE OFFICE, ON THE SCREEN (ops #33, part A).
 *
 * A text with a stop phrase inside a longer sentence is held (ops #30): every text to her number is
 * refused until somebody decides, or until five business days pass and it becomes a mark on every
 * channel. This card is where somebody decides, drawn on the Texts screen's band and on her card on
 * the household page, the same card in both:
 *
 *   A GOLD PILL, NEVER CORAL. A hold is a decision waiting, like a rule that fired, not a gap.
 *   HER WORDS, the whole text as the list draws it, a child where React escapes it, and the phrase
 *   that was found under it: found, never a quotation of her, because "Don't stop texting me" holds
 *   "stop texting me" and says the opposite. A text a forget blanked says so, and the phrase stays.
 *   WHAT THEY ASKED, texts or every channel, and THE CLOCK, in the days the server worked out in
 *   Florida.
 *   THE TWO BUTTONS, one dialog open at a time under the card:
 *     Confirm it as a stop   anybody in the office. Her words' own channel is chosen; reading them
 *                            wider takes a reason, and reading "leave me alone" as texts only is an
 *                            owner's or a manager's, with a reason.
 *     Clear it: no stop      an owner or a manager (`canClear`), with a reason, always.
 *
 * HIDING CLEAR IS UX, NEVER THE CONTROL: `api/src/routes/stop-holds.ts` refuses a clear and a
 * narrowing from anybody else whatever this draws (binding rule 6), and both read
 * `MARK_LIFTING_ROLES`. A decision that lost to another, or to the tick, is said in the server's own
 * words, and the screen reads again.
 */
import { useState, type ReactNode } from 'react';
import type { StopWordsChannel } from '@fk/shared';
import { ApiError, decideStopHold, type StopHoldView } from '../api.ts';
import {
  BUSINESS_DAYS_WORDS,
  HOLD_PILL_WORDS,
  askedWords,
  clearedWords,
  confirmedWords,
  foundWords,
  holdClockWords,
} from '../holds.ts';
import { TextBody } from '../screens/TextThread.tsx';
import { MEASURE, Pill } from './AppShell.tsx';
import { Button, TextAreaField } from './Field.tsx';
import { ErrorNotice } from './Notice.tsx';

/** What the server says when the hold was decided meanwhile: shown, and the screen reads again. */
const ALREADY_DECIDED = 'already_decided';

/** The two ways a confirmation reads her words, in the order the dialog offers them. */
const READINGS: readonly { readonly channel: StopWordsChannel; readonly label: string }[] = [
  { channel: 'sms', label: 'Texts only' },
  { channel: 'all', label: 'Every way' },
];

/** A confirmation on another channel than her words, with no reason typed. */
const SAY_WHY_THAT_WAY =
  'Say why you read their words that way, so whoever reads the hold later knows.';

/** A clear with no reason typed. */
const SAY_WHY_NO_STOP = 'Say why their words were no stop, so whoever reads the hold later knows.';

type Opened = 'confirm' | 'clear' | null;

export function StopHoldCard({
  hold,
  sender,
  body,
  canClear,
  onDone,
  children,
}: {
  hold: StopHoldView;
  /** Who sent it, by the text's own names and never the number the hold keeps. Null on her card. */
  sender: string | null;
  /** Her whole text, or null once a forget blanked it. */
  body: string | null;
  /** An owner or a manager, as the server decides it anyway. */
  canClear: boolean;
  /** Told the sentence to show once a decision landed, or lost to another; the caller reads again. */
  onDone: (sentence: string) => void;
  /** Links under her words: her household, her enquiry, the phone system's page. */
  children?: ReactNode;
}) {
  const [opened, setOpened] = useState<Opened>(null);
  const forgotten = body === null || body === '';
  return (
    <div
      className="rounded-lg border border-gold/50 border-l-4 border-l-gold bg-parchment p-3"
      data-hold={hold.id}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone="gold">{HOLD_PILL_WORDS}</Pill>
      </div>
      {sender ? (
        <h3 className="mt-2 font-serif text-lg leading-tight wrap-anywhere">{sender}</h3>
      ) : null}
      {forgotten ? null : <TextBody body={body} />}
      <p className={`mt-1 text-sm ${MEASURE}`}>{foundWords(hold, forgotten)}</p>
      <p className={`mt-2 text-sm ${MEASURE}`}>{askedWords(hold)}</p>
      <p className={`mt-2 text-sm ${MEASURE}`}>{holdClockWords(hold)}</p>
      <p className="mt-1 text-xs text-navy/65">{BUSINESS_DAYS_WORDS}</p>
      {children}

      <div className="mt-3 flex flex-wrap gap-2 border-t border-navy/10 pt-3">
        <Button
          tone="quiet"
          onClick={() => setOpened((now) => (now === 'confirm' ? null : 'confirm'))}
        >
          Confirm it as a stop
        </Button>
        {canClear ? (
          <Button
            tone="quiet"
            onClick={() => setOpened((now) => (now === 'clear' ? null : 'clear'))}
          >
            Clear it: no stop
          </Button>
        ) : null}
      </div>

      {opened === 'confirm' ? (
        <ConfirmHold
          hold={hold}
          canNarrow={canClear}
          onDone={onDone}
          onCancel={() => setOpened(null)}
        />
      ) : null}
      {opened === 'clear' && canClear ? (
        <ClearHold hold={hold} onDone={onDone} onCancel={() => setOpened(null)} />
      ) : null}
    </div>
  );
}

/** Sends one decision, and turns its answer, or a refusal, into what the card says next. */
function useDecision(onDone: (sentence: string) => void) {
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function run(work: () => Promise<string>) {
    setBusy(true);
    setProblem(null);
    try {
      onDone(await work());
    } catch (thrown) {
      if (thrown instanceof ApiError && thrown.code === ALREADY_DECIDED) {
        onDone(thrown.message);
        return;
      }
      setProblem(thrown instanceof ApiError ? thrown.message : 'That was not written down.');
    } finally {
      setBusy(false);
    }
  }
  return { problem, setProblem, busy, run };
}

/** Confirm it as a stop: anybody in the office, on her words' own channel unless said otherwise. */
function ConfirmHold({
  hold,
  canNarrow,
  onDone,
  onCancel,
}: {
  hold: StopHoldView;
  canNarrow: boolean;
  onDone: (sentence: string) => void;
  onCancel: () => void;
}) {
  const field = (name: string) => `hold-${hold.id}-${name}`;
  const [channel, setChannel] = useState<StopWordsChannel>(hold.wordsChannel);
  const [note, setNote] = useState('');
  const { problem, setProblem, busy, run } = useDecision(onDone);
  const differs = channel !== hold.wordsChannel;
  // Reading "leave me alone" as texts only leaves calls and email open: a lift's act.
  const narrowingRefused = (reading: StopWordsChannel) =>
    hold.wordsChannel === 'all' && reading === 'sms' && !canNarrow;

  function confirm() {
    const said = note.trim();
    if (differs && said === '') {
      setProblem(SAY_WHY_THAT_WAY);
      return;
    }
    void run(async () => {
      await decideStopHold(hold.id, {
        outcome: 'confirmed',
        channel,
        note: said === '' ? null : said,
      });
      return confirmedWords(channel);
    });
  }

  return (
    <div className="mt-3 rounded-lg border border-navy/20 p-3">
      <fieldset>
        <legend className="text-sm font-medium">How their words read</legend>
        <div className="mt-2 flex flex-col gap-2">
          {READINGS.map((reading) => {
            const refused = narrowingRefused(reading.channel);
            return (
              <label
                key={reading.channel}
                htmlFor={field(`reading-${reading.channel}`)}
                className={`flex min-h-11 desk:min-h-9 items-center gap-3 rounded-lg border p-3 ${
                  channel === reading.channel ? 'border-navy bg-gold/10' : 'border-navy/25'
                } ${refused ? 'opacity-60' : 'cursor-pointer'}`}
              >
                <input
                  id={field(`reading-${reading.channel}`)}
                  type="radio"
                  name={field('reading')}
                  checked={channel === reading.channel}
                  disabled={refused}
                  onChange={() => setChannel(reading.channel)}
                  className="h-5 w-5 shrink-0 accent-navy"
                />
                <span className="text-sm">{reading.label}</span>
              </label>
            );
          })}
        </div>
        {narrowingRefused('sms') ? (
          <p className="mt-2 text-xs text-navy/65">
            Only an owner or a manager can read &apos;every way&apos; as texts only.
          </p>
        ) : null}
      </fieldset>

      <div className="mt-3">
        <TextAreaField
          id={field('note')}
          label="Their words, or why"
          value={note}
          onChange={setNote}
          rows={2}
          hint={
            differs
              ? 'Needed, because this reads their words another way than they say. It stays on the hold with your name.'
              : 'Only if you want to add something. It stays on the hold with your name.'
          }
        />
      </div>

      <p className={`mb-3 text-sm ${MEASURE}`}>
        Their number is marked do not contact, recorded by you with the day they said it; the phone
        system is told; anything still on its way to them is withdrawn. Only an owner or a manager
        can lift it.
      </p>

      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not written down" message={problem} />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button onClick={confirm} disabled={busy}>
          {busy ? 'Writing it down' : 'Confirm the stop'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Never mind
        </Button>
      </div>
    </div>
  );
}

/** Clear it: no stop. An owner or a manager, with a reason, and nothing lifted by it. */
function ClearHold({
  hold,
  onDone,
  onCancel,
}: {
  hold: StopHoldView;
  onDone: (sentence: string) => void;
  onCancel: () => void;
}) {
  const field = (name: string) => `hold-${hold.id}-${name}`;
  const [reason, setReason] = useState('');
  const { problem, setProblem, busy, run } = useDecision(onDone);

  function clear() {
    const said = reason.trim();
    if (said === '') {
      setProblem(SAY_WHY_NO_STOP);
      return;
    }
    void run(async () => {
      const answer = await decideStopHold(hold.id, { outcome: 'cleared', note: said });
      return clearedWords(answer.outcome === 'cleared' ? answer.stillRefused : null);
    });
  }

  return (
    <div className="mt-3 rounded-lg border border-coral/50 border-l-4 border-l-coral bg-parchment p-3">
      <TextAreaField
        id={field('reason')}
        label="Why their words were no stop"
        value={reason}
        onChange={setReason}
        rows={2}
        hint="It stays on the hold with your name."
      />
      <p className={`mb-3 text-sm ${MEASURE}`}>
        Texts to their number go again on the website box they ticked, with no fresh opt-in
        confirmation, because the phone system never refused them.
      </p>

      {problem ? (
        <div className="mb-3">
          <ErrorNotice title="Not written down" message={problem} />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button onClick={clear} disabled={busy}>
          {busy ? 'Writing it down' : 'Clear it'}
        </Button>
        <Button tone="quiet" onClick={onCancel} disabled={busy}>
          Never mind
        </Button>
      </div>
    </div>
  );
}
